import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ResourcePanel = ({ workers, onClose }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterSkill, setFilterSkill] = useState('all');
  const [filterAvailability, setFilterAvailability] = useState('all');

  const allSkills = [...new Set(workers?.flatMap(w => w?.skills || []))];

  const filteredWorkers = workers?.filter(worker => {
    const matchesSearch = worker?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         worker?.role?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesSkill = filterSkill === 'all' || worker?.skills?.includes(filterSkill);
    const matchesAvailability = filterAvailability === 'all' || worker?.availability === filterAvailability;
    return matchesSearch && matchesSkill && matchesAvailability;
  });

  const getAvailabilityColor = (availability) => {
    switch (availability) {
      case 'available':
        return 'var(--color-success)';
      case 'partial':
        return 'var(--color-warning)';
      case 'unavailable':
        return 'var(--color-error)';
      default:
        return 'var(--color-muted-foreground)';
    }
  };

  const getUtilizationPercentage = (assigned, capacity) => {
    return Math.round((assigned / capacity) * 100);
  };

  const getUtilizationColor = (percentage) => {
    if (percentage >= 100) return 'var(--color-error)';
    if (percentage >= 75) return 'var(--color-warning)';
    return 'var(--color-success)';
  };

  return (
    <div className="bg-card rounded-xl border border-border h-full flex flex-col">
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Resource Pool</h3>
          <button
            className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-muted transition-colors duration-250"
            onClick={onClose}
            aria-label="Close panel"
          >
            <Icon name="X" size={16} />
          </button>
        </div>

        <div className="space-y-3">
          <div className="relative">
            <Icon
              name="Search"
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <input
              type="text"
              placeholder="Search workers..."
              className="w-full pl-10 pr-4 py-2 bg-muted rounded-lg text-sm text-foreground placeholder:text-muted-foreground border-none outline-none focus:ring-2 focus:ring-primary"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
            />
          </div>

          <div className="flex gap-2">
            <select
              className="flex-1 px-3 py-2 bg-muted rounded-lg text-xs text-foreground border-none outline-none cursor-pointer"
              value={filterSkill}
              onChange={(e) => setFilterSkill(e?.target?.value)}
            >
              <option value="all">All Skills</option>
              {allSkills?.map((skill) => (
                <option key={skill} value={skill}>
                  {skill}
                </option>
              ))}
            </select>

            <select
              className="flex-1 px-3 py-2 bg-muted rounded-lg text-xs text-foreground border-none outline-none cursor-pointer"
              value={filterAvailability}
              onChange={(e) => setFilterAvailability(e?.target?.value)}
            >
              <option value="all">All Status</option>
              <option value="available">Available</option>
              <option value="partial">Partial</option>
              <option value="unavailable">Unavailable</option>
            </select>
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {filteredWorkers?.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Icon name="Users" size={48} color="var(--color-muted-foreground)" />
            <p className="mt-4 text-sm text-muted-foreground">No workers found</p>
          </div>
        ) : (
          filteredWorkers?.map((worker) => {
            const utilizationPercentage = getUtilizationPercentage(worker?.assigned, worker?.capacity);
            const utilizationColor = getUtilizationColor(utilizationPercentage);

            return (
              <div
                key={worker?.id}
                className="p-3 bg-muted rounded-lg hover:bg-muted/80 transition-colors duration-250 cursor-pointer"
                draggable
                onDragStart={(e) => {
                  e?.dataTransfer?.setData('workerId', worker?.id?.toString());
                  e.dataTransfer.effectAllowed = 'move';
                }}
              >
                <div className="flex items-start gap-3 mb-3">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold text-primary-foreground flex-shrink-0"
                    style={{ backgroundColor: getAvailabilityColor(worker?.availability) }}
                  >
                    {worker?.avatar}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{worker?.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{worker?.role}</p>
                    <div className="flex items-center gap-1 mt-1">
                      <div
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: getAvailabilityColor(worker?.availability) }}
                      />
                      <span className="text-xs capitalize text-muted-foreground">
                        {worker?.availability}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="mb-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-muted-foreground">Capacity</span>
                    <span className="text-xs font-medium" style={{ color: utilizationColor }}>
                      {worker?.assigned}h / {worker?.capacity}h ({utilizationPercentage}%)
                    </span>
                  </div>
                  <div className="w-full h-1.5 bg-background rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-250"
                      style={{
                        width: `${Math.min(utilizationPercentage, 100)}%`,
                        backgroundColor: utilizationColor
                      }}
                    />
                  </div>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-2">Skills</p>
                  <div className="flex flex-wrap gap-1">
                    {worker?.skills?.map((skill, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-0.5 bg-background rounded text-[10px] text-foreground"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
      <div className="p-4 border-t border-border">
        <div className="grid grid-cols-3 gap-2 mb-3">
          <div className="text-center">
            <p className="text-xs text-muted-foreground">Available</p>
            <p className="text-lg font-semibold text-foreground">
              {workers?.filter(w => w?.availability === 'available')?.length}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs text-muted-foreground">Partial</p>
            <p className="text-lg font-semibold text-foreground">
              {workers?.filter(w => w?.availability === 'partial')?.length}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs text-muted-foreground">Busy</p>
            <p className="text-lg font-semibold text-foreground">
              {workers?.filter(w => w?.availability === 'unavailable')?.length}
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" iconName="UserPlus" fullWidth>
          Add Worker
        </Button>
      </div>
    </div>
  );
};

export default ResourcePanel;